/**
 * Salesforce Products — apps.leferi.com/salesforce-products
 *
 * 기회 상품(OpportunityLineItem) 대량 업로드. 엑셀 업로드 → 미리보기(파싱 + ID 해석,
 * 쓰기 없음) → 확인 후 실제 create. 프로덕션 Salesforce 에 직접 쓰기이므로 반드시
 * 미리보기로 검증한 뒤 "생성"을 눌러야 create 된다.
 *
 * chrome 은 PdfCompressor 미러링 — 사각 모서리, 핑크 CTA, brown ink, ▪ 마커, no emoji.
 */
import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, FileSpreadsheet, Upload, X as XIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { AuthSession } from './auth'
import {
  commitOppProducts,
  getSalesforceStatus,
  getSheetNames,
  humanizeSalesforceError,
  previewOppProducts,
  SalesforceError,
  type CommitResult,
  type PreviewResult,
  type SalesforceStatus,
} from './salesforceApi'

type Phase = 'landing' | 'previewing' | 'preview' | 'committing' | 'complete'

export function SalesforceProductsPage({ session: _session }: { session: AuthSession }) {
  const [status, setStatus] = useState<SalesforceStatus | null>(null)
  const [statusErr, setStatusErr] = useState<string | null>(null)
  const [template, setTemplate] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [sheets, setSheets] = useState<string[]>([])
  const [sheet, setSheet] = useState('')
  const [phase, setPhase] = useState<Phase>('landing')
  const [preview, setPreview] = useState<PreviewResult | null>(null)
  const [commit, setCommit] = useState<CommitResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    getSalesforceStatus()
      .then((s) => {
        setStatus(s)
        setTemplate((prev) => prev || s.templates[0]?.key || '')
      })
      .catch((e: unknown) =>
        setStatusErr(e instanceof SalesforceError ? humanizeSalesforceError(e.message) : '상태 조회 실패'),
      )
  }, [])

  const activeTemplate = status?.templates.find((t) => t.key === template)

  // 파일 선택 시 시트 목록을 읽어 여러 탭이면 선택할 수 있게 한다(첫 시트 기본).
  const chooseFile = (f: File | null) => {
    setFile(f)
    setSheets([])
    setSheet('')
    setError(null)
    if (!f) return
    getSheetNames(f)
      .then((names) => {
        setSheets(names)
        setSheet(names[0] ?? '')
      })
      .catch(() => {
        /* 시트 조회 실패는 무시 — 첫 시트로 진행 */
      })
  }

  const runPreview = async () => {
    if (!file || !template) return
    setError(null)
    setPhase('previewing')
    try {
      const r = await previewOppProducts(template, file, sheet || undefined)
      setPreview(r)
      setPhase('preview')
    } catch (e) {
      setError(e instanceof SalesforceError ? humanizeSalesforceError(e.message) : '미리보기 실패')
      setPhase('landing')
    }
  }

  const runCommit = async () => {
    if (!file || !template) return
    setError(null)
    setPhase('committing')
    try {
      const r = await commitOppProducts(template, file, sheet || undefined)
      setCommit(r)
      setPhase('complete')
    } catch (e) {
      setError(e instanceof SalesforceError ? humanizeSalesforceError(e.message) : '생성 실패')
      setPhase('preview')
    }
  }

  const reset = () => {
    setFile(null)
    setSheets([])
    setSheet('')
    setPreview(null)
    setCommit(null)
    setError(null)
    setPhase('landing')
  }

  return (
    <div className="pt-s-7">
      <Link
        to="/?tab=leferist"
        className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:bg-surface-soft hover:text-fg-1 focus-visible:outline-none focus-visible:shadow-focus"
      >
        <ChevronLeft size={14} strokeWidth={1.5} />
        Apps
      </Link>

      <div className="mt-s-6">
        <p className="lf-eyebrow">▪ Salesforce · 상품 대량 업로드</p>
        <h1 className="mt-s-2 lf-display-2">Bulk upload opportunity products.</h1>
        <p className="mt-s-3 max-w-[680px] lf-body text-fg-2">
          엑셀을 올리면 각 행을 기회 상품(OpportunityLineItem)으로 만들어요. 판매상품·크리에이터·
          담당자는 이름으로 Salesforce에서 찾아 연결해요. <span className="text-fg-1">미리보기로 검증한 뒤</span>{' '}
          생성 버튼을 눌러야 실제로 반영돼요.
        </p>
      </div>

      {statusErr ? <p className="mt-s-4 lf-body-sm text-status-danger">▪ {statusErr}</p> : null}
      {status && !status.configured ? (
        <div className="mt-s-5 border border-line-hairline bg-surface-soft p-s-4">
          <p className="lf-eyebrow text-fg-3">▪ 설정 필요</p>
          <p className="mt-s-2 lf-body-sm text-fg-2">
            서버에 Salesforce 자격증명(<span className="lf-num">SF_USERNAME</span>,{' '}
            <span className="lf-num">SF_PASSWORD</span> 등)이 설정되지 않았어요. .env / Render env에 추가하면
            동작해요.
          </p>
        </div>
      ) : null}

      {(phase === 'landing' || phase === 'previewing') && status ? (
        <LandingForm
          templates={status.templates}
          template={template}
          onTemplate={setTemplate}
          activeTemplateColumns={activeTemplate?.requiredColumns ?? []}
          file={file}
          onFile={chooseFile}
          sheets={sheets}
          sheet={sheet}
          onSheet={setSheet}
          busy={phase === 'previewing'}
          onPreview={runPreview}
          error={error}
        />
      ) : null}

      {phase === 'preview' && preview ? (
        <PreviewView
          result={preview}
          fileName={file?.name ?? ''}
          onCommit={runCommit}
          onBack={reset}
          error={error}
        />
      ) : null}

      {phase === 'committing' ? (
        <section className="mt-s-7 border border-line-hairline p-s-6">
          <p className="lf-eyebrow">▪ Creating · 생성 중</p>
          <p className="mt-s-2 lf-body text-fg-2">Salesforce에 기회 상품을 생성하고 있어요. 잠시만요…</p>
        </section>
      ) : null}

      {phase === 'complete' && commit ? <CompleteView result={commit} onReset={reset} /> : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Landing: template + upload                                                  */
/* -------------------------------------------------------------------------- */

function LandingForm({
  templates,
  template,
  onTemplate,
  activeTemplateColumns,
  file,
  onFile,
  sheets,
  sheet,
  onSheet,
  busy,
  onPreview,
  error,
}: {
  templates: SalesforceStatus['templates']
  template: string
  onTemplate: (k: string) => void
  activeTemplateColumns: string[]
  file: File | null
  onFile: (f: File | null) => void
  sheets: string[]
  sheet: string
  onSheet: (s: string) => void
  busy: boolean
  onPreview: () => void
  error: string | null
}) {
  const [dragOver, setDragOver] = useState(false)
  const fileRef = useRef<HTMLInputElement | null>(null)

  const choose = (f: File | null) => {
    if (!f) return
    if (!/\.(xlsx|xls|csv)$/i.test(f.name)) return
    onFile(f)
  }

  return (
    <div className="mt-s-7 flex flex-col gap-s-6 border border-line-hairline p-s-6">
      <Field label="Template · 템플릿">
        <div className="flex flex-col gap-s-2">
          {templates.map((t) => {
            const selected = t.key === template
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => onTemplate(t.key)}
                className={`flex w-full flex-col items-start gap-s-1 rounded-r-1 border px-s-4 py-s-3 text-left transition-colors duration-base ease-standard focus-visible:outline-none focus-visible:shadow-focus ${
                  selected
                    ? 'border-brand-pink bg-pink-50'
                    : 'border-line-hairline bg-surface-base hover:border-line-soft'
                }`}
              >
                <span className="lf-h4 text-fg-1">{t.label}</span>
                <span className="lf-body-sm text-fg-2">{t.description}</span>
              </button>
            )
          })}
        </div>
      </Field>

      {activeTemplateColumns.length ? (
        <Field label="Required columns · 필수 컬럼" hint="이 헤더가 엑셀 첫 행에 있어야 해요.">
          <div className="flex flex-wrap gap-s-2">
            {activeTemplateColumns.map((c) => (
              <span
                key={c}
                className="rounded-r-1 border border-line-hairline bg-surface-soft px-s-2 py-px lf-caption text-fg-2"
              >
                {c}
              </span>
            ))}
          </div>
        </Field>
      ) : null}

      <Field label="File · 엑셀 (.xlsx / .csv)">
        <div
          onDragEnter={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragOver(false)
            choose(e.dataTransfer.files?.[0] ?? null)
          }}
          className={`flex flex-col items-center justify-center gap-s-3 border-dashed border-2 px-s-4 py-s-9 transition-colors duration-base ease-standard ${
            dragOver ? 'border-brand-pink bg-pink-50' : 'border-line-hairline bg-surface-base'
          }`}
        >
          {file ? (
            <>
              <FileSpreadsheet size={32} strokeWidth={1.4} className="text-fg-2" />
              <p className="lf-body text-fg-1">{file.name}</p>
              <button
                type="button"
                onClick={() => {
                  onFile(null)
                  if (fileRef.current) fileRef.current.value = ''
                }}
                className="inline-flex items-center gap-s-1 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
              >
                <XIcon size={13} strokeWidth={1.5} />
                Remove
              </button>
            </>
          ) : (
            <>
              <Upload size={28} strokeWidth={1.4} className="text-fg-3" />
              <p className="lf-body text-fg-2">엑셀을 여기로 드래그하거나</p>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-1 transition-colors duration-base ease-standard hover:border-line-soft"
              >
                파일 선택 · Choose file
              </button>
            </>
          )}
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => choose(e.target.files?.[0] ?? null)}
          />
        </div>
      </Field>

      {file && sheets.length > 1 ? (
        <Field
          label="Sheet · 시트 선택"
          hint={`탭이 ${sheets.length}개인 파일이에요. 업로드할 시트를 고르세요.`}
        >
          <select
            value={sheet}
            onChange={(e) => onSheet(e.target.value)}
            className="w-full rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-3 lf-body text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus"
          >
            {sheets.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
      ) : null}

      <div className="flex flex-wrap items-center gap-s-4">
        <button
          type="button"
          disabled={!file || !template || busy}
          onClick={onPreview}
          className="inline-flex items-center justify-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-3 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? 'Checking…' : '미리보기 · Preview'}
        </button>
        <p className="lf-eyebrow flex items-start gap-s-2 text-fg-3">
          <span aria-hidden>▪</span>
          <span>미리보기는 Salesforce를 읽기만 해요. 생성은 다음 단계에서 확인 후 진행돼요.</span>
        </p>
      </div>

      {error ? <p className="lf-body-sm text-status-danger">▪ {error}</p> : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Preview                                                                     */
/* -------------------------------------------------------------------------- */

function PreviewView({
  result,
  fileName,
  onCommit,
  onBack,
  error,
}: {
  result: PreviewResult
  fileName: string
  onCommit: () => void
  onBack: () => void
  error: string | null
}) {
  const { summary, rows } = result
  return (
    <section className="mt-s-7 border border-line-hairline p-s-6">
      <p className="lf-eyebrow">▪ Preview · 미리보기</p>
      <h2 className="mt-s-2 lf-h2 text-fg-1">{fileName || '업로드한 파일'}</h2>

      <div className="mt-s-5 grid grid-cols-3 gap-s-3 sm:max-w-[480px]">
        <Stat label="전체 · Total" value={String(summary.total)} />
        <Stat label="생성 대상 · Ready" value={String(summary.ok)} tone="positive" />
        <Stat label="문제 · Errors" value={String(summary.error)} tone={summary.error ? 'danger' : 'neutral'} />
      </div>

      <RowTable
        rows={rows.map((r) => ({
          rowNum: r.rowNum,
          opportunityId: r.opportunityId,
          status: r.status,
          detail: r.status === 'ok' ? '생성 준비됨' : r.errors.join(' · '),
        }))}
      />

      <div className="mt-s-4 border-l-2 border-l-status-warning bg-surface-soft px-s-4 py-s-3">
        <p className="lf-body-sm text-fg-1">
          ▪ 아래 버튼을 누르면 <span className="text-status-warning">프로덕션 Salesforce</span>에{' '}
          <span className="lf-num">{summary.ok}</span>건이 실제로 생성돼요. 문제 행({summary.error})은 건너뛰어요.
        </p>
      </div>

      <div className="mt-s-5 flex flex-wrap gap-s-3">
        <button
          type="button"
          disabled={summary.ok === 0}
          onClick={onCommit}
          className="inline-flex items-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-3 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Salesforce에 생성 · Create ({summary.ok})
        </button>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
        >
          다시 선택 · Back
        </button>
      </div>

      {error ? <p className="mt-s-4 lf-body-sm text-status-danger">▪ {error}</p> : null}
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* Complete                                                                    */
/* -------------------------------------------------------------------------- */

function CompleteView({ result, onReset }: { result: CommitResult; onReset: () => void }) {
  const { summary, rows } = result
  return (
    <section className="mt-s-7 border border-line-hairline p-s-6">
      <p className="lf-eyebrow">▪ Done · 완료</p>
      <h2 className="mt-s-2 lf-h2 text-fg-1">대량 생성 결과</h2>

      <div className="mt-s-5 grid grid-cols-3 gap-s-3 sm:max-w-[480px]">
        <Stat label="생성 · Created" value={String(summary.created)} tone="positive" />
        <Stat label="실패 · Failed" value={String(summary.failed)} tone={summary.failed ? 'danger' : 'neutral'} />
        <Stat label="건너뜀 · Skipped" value={String(summary.skipped)} />
      </div>

      <RowTable
        rows={rows.map((r) => ({
          rowNum: r.rowNum,
          opportunityId: r.opportunityId,
          status: r.status,
          detail:
            r.status === 'created'
              ? (r.id ?? '생성됨')
              : r.status === 'error'
                ? `건너뜀 · ${r.errors.join(' · ')}`
                : r.errors.join(' · '),
        }))}
      />

      <div className="mt-s-5">
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
        >
          새로 시작 · Upload another
        </button>
      </div>
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* Atoms                                                                       */
/* -------------------------------------------------------------------------- */

type RowStatus = 'ok' | 'error' | 'created' | 'failed'

function RowTable({
  rows,
}: {
  rows: { rowNum: number; opportunityId: string; status: RowStatus; detail: string }[]
}) {
  return (
    <div className="mt-s-5 overflow-x-auto border border-line-hairline">
      <table className="w-full border-separate border-spacing-0">
        <thead>
          <tr>
            <Th>행</Th>
            <Th>기회 ID</Th>
            <Th>상태</Th>
            <Th>상세</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.rowNum}-${i}`}>
              <Td className="lf-num text-fg-3">{r.rowNum}</Td>
              <Td className="lf-num text-fg-2">{r.opportunityId || '—'}</Td>
              <Td>
                <StatusBadge status={r.status} />
              </Td>
              <Td className="text-fg-2">{r.detail || '—'}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function StatusBadge({ status }: { status: RowStatus }) {
  const map: Record<RowStatus, { label: string; cls: string }> = {
    ok: { label: 'Ready', cls: 'border-brand-blue text-brand-blue' },
    created: { label: 'Created', cls: 'border-brand-blue text-brand-blue' },
    error: { label: 'Error', cls: 'border-status-danger text-status-danger' },
    failed: { label: 'Failed', cls: 'border-status-danger text-status-danger' },
  }
  const s = map[status]
  return (
    <span className={`inline-flex rounded-r-1 border px-s-2 py-px lf-eyebrow uppercase tracking-wide ${s.cls}`}>
      {s.label}
    </span>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="whitespace-nowrap border-b border-line-soft bg-surface-soft px-s-3 py-s-2 text-left lf-caption text-fg-3">
      {children}
    </th>
  )
}

function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <td className={`whitespace-nowrap border-b border-line-hairline px-s-3 py-s-2 lf-body-sm ${className}`}>
      {children}
    </td>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <p className="lf-eyebrow text-fg-3">▪ {label}</p>
      <div className="mt-s-3">{children}</div>
      {hint ? <p className="mt-s-2 lf-caption text-fg-3">{hint}</p> : null}
    </div>
  )
}

function Stat({
  label,
  value,
  tone = 'neutral',
}: {
  label: string
  value: string
  tone?: 'positive' | 'danger' | 'neutral'
}) {
  const color = tone === 'positive' ? 'text-brand-blue' : tone === 'danger' ? 'text-status-danger' : 'text-fg-1'
  return (
    <div className="border border-line-hairline bg-surface-base p-s-4">
      <p className="lf-eyebrow text-fg-3">{label}</p>
      <p className={`mt-s-2 lf-h2 lf-num ${color}`}>{value}</p>
    </div>
  )
}
